import type { AppContext } from '../schema/AppContext';
import type { AppContextSignal } from '../schema/AppContextSignal';
import type { AppError } from '../schema/AppError';
import { createAppContext } from '../utils/createAppContext';
import type { LogOptions } from 'telemetry';
import { useCallback, useEffect, useRef } from 'react';
import { useReactAppContextRef } from '../context/ReactAppCtx';

export const useAppContextSignal = (): AppContextSignal => {
    const appCtxRef = useReactAppContextRef();
    return useCallback<AppContextSignal>(
        (
            name: string,
            /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
             * See aka.ms/mini-lint-errors
             *	> Unexpected any. Specify a different type. */
            inputPayload?: Record<string, any>,
            options?: LogOptions,
            error?: AppError
        ): void => {
            appCtxRef?.current?.signal(name, inputPayload, options, error);
        },
        [appCtxRef]
    );
};

export const useUpdateDefaultActivePayload: <T>() => (updatedPayload: T) => void = <T>() => {
    const appCtxRef = useReactAppContextRef();
    return useCallback(
        (updatedPayload: T) => {
            appCtxRef?.current?.updateDefaultPayload<T>({ ...updatedPayload });
        },
        [appCtxRef]
    );
};

export const useNewAppContextRef = <T>(
    {
        defaultPayload,
        cid,
    }: {
        defaultPayload?: T;
        cid?: string;
    },
    customContextRef?: React.MutableRefObject<AppContext>
): [React.MutableRefObject<AppContext>, () => void] => {
    const newContextRef = useRef<AppContext>(createAppContext(cid, defaultPayload));
    const effectiveContextRef = customContextRef ?? newContextRef;

    const resetAppContextRef = useCallback(() => {
        effectiveContextRef.current = createAppContext(cid, defaultPayload);
    }, [cid, defaultPayload, effectiveContextRef]);

    useEffect(() => {
        effectiveContextRef.current?.updateDefaultPayload({ ...defaultPayload });
    }, [defaultPayload, effectiveContextRef]);

    return [effectiveContextRef, resetAppContextRef];
};
